import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert, CSpinner,
  CTabContent, CTabPane, CNav, CNavItem, CNavLink,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CCard, CCardHeader, CCardBody,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'
import { simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnasCategorias: WwColumn[] = [
  { key: 'categoriaNro', title: 'Nro', type: 'number' },
  { key: 'categoriaNombre', title: 'Nombre' },
  { key: 'categoriaDescripcion', title: 'Descripcion' },
]

const columnasSubCategorias: WwColumn[] = [
  { key: 'categoriaNro', title: 'Categoria Nro', type: 'number' },
  { key: 'categoriaNombre', title: 'Categoria Nombre' },
  { key: 'categoriaDescripcion', title: 'Categoria Descripcion' },
]

const columnasProductos: WwColumn[] = [
  { key: 'productoNro', title: 'Nro', type: 'number' },
  { key: 'productoFecha', title: 'Fecha', type: 'date' },
  { key: 'productoHora', title: 'Hora' },
  { key: 'productoFechaSistema', title: 'Fecha Sistema', type: 'date' },
  { key: 'productoNombre', title: 'Nombre' },
  { key: 'productoDescripcion', title: 'Descripcion' },
  { key: 'productoValor', title: 'Valor', type: 'currency' },
  { key: 'productoStock', title: 'Stock', type: 'currency' },
  { key: 'productoDiaVenta', title: 'Dia Venta', type: 'number' },
  { key: 'productoLibreValor1', title: 'Libre Valor1', type: 'currency' },
  { key: 'productoLIbreValor2', title: 'LIbre Valor2', type: 'currency' },
  { key: 'productoLibreValor3', title: 'Libre Valor3', type: 'currency' },
  { key: 'productoLibreNumero1', title: 'Libre Numero1', type: 'number' },
  { key: 'productoLibreNumero2', title: 'Libre Numero2', type: 'number' },
  { key: 'productoLibreNumero3', title: 'Libre Numero3', type: 'number' },
  { key: 'productoLibreCaracter1', title: 'Libre Caracter1' },
  { key: 'productoLibreCaracter2', title: 'Libre Caracter2' },
  { key: 'subCategoriaNro', title: 'Sub Categoria Nro', type: 'number' },
  { key: 'subCategoriaNombre', title: 'Sub Categoria Nombre' },
  { key: 'categoriaNro', title: 'Categoria Nro', type: 'number' },
  { key: 'categoriaNombre', title: 'Categoria Nombre' },
]

const CatalogosPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const navigate = useNavigate()
  const [tab, setTab] = useState('categorias')
  const catFormRef = useRef<HTMLFormElement>(null)
  const subFormRef = useRef<HTMLFormElement>(null)
  const prodFormRef = useRef<HTMLFormElement>(null)
  const [categorias, setCategorias] = useState<any[]>([])
  const [subCategorias, setSubCategorias] = useState<any[]>([])
  const [productos, setProductos] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const { aviso, setAviso } = usePendiente()
  const [modal, setModal] = useState<'cat' | 'sub' | 'prod' | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  // Fila en edición (categoriaww.cs:2295 'DO UPDATE' → webobject categoria Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [cat, setCat] = useState({ empresaCodigo: empresa, nombre: '', descripcion: '', imagen: '' })
  const [sub, setSub] = useState({ empresaCodigo: empresa, nombre: '', categoriaNro: '', descripcion: '', imagen: '' })
  const [prod, setProd] = useState({
    empresaCodigo: empresa, nombre: '', descripcion: '', valor: '', stock: '', subCategoriaNro: '',
    diaVenta: '',
    libreValor1: '', libreValor2: '', libreValor3: '',
    libreNumero1: '', libreNumero2: '', libreNumero3: '',
    libreCaracter1: '', libreCaracter2: '', imagen: '',
  })

  const cargar = () => {
    setCargando(true)
    Promise.all([
      api.get<any[]>('/catalogos/categorias?empresa=' + empresa),
      api.get<any[]>('/catalogos/subcategorias?empresa=' + empresa),
      api.get<any[]>('/catalogos/productos?empresa=' + empresa),
    ])
      .then(([c, s, p]) => {
        setCategorias(c)
        setSubCategorias(s)
        setProductos(p)
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const guardar = async (e: React.FormEvent, fn: () => Promise<any>) => {
    e.preventDefault()
    setErrorModal('')
    setGuardando(true)
    try {
      await fn()
      setModal(null)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setErrorModal(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const guardarCategoria = () => {
    const body = {
      empresaCodigo: cat.empresaCodigo, nombre: cat.nombre,
      descripcion: cat.descripcion || undefined,
      categoriaImagen: cat.imagen || undefined,
    }
    // UPD: la llave va en la URL (categoria_bc.cs:396-432, BC00078)
    return editando
      ? api.put(`/catalogos/categorias/${editando.categoriaNro}?empresa=${cat.empresaCodigo}`, body)
      : api.post('/catalogos/categorias', body)
  }

  const guardarSubCategoria = () => {
    const body = {
      empresaCodigo: sub.empresaCodigo, nombre: sub.nombre,
      categoriaNro: Number(sub.categoriaNro),
      descripcion: sub.descripcion || undefined,
      subCategoriaImagen: sub.imagen || undefined,
    }
    return editando
      ? api.put(`/catalogos/subcategorias/${editando.subCategoriaNro}?empresa=${sub.empresaCodigo}`, body)
      : api.post('/catalogos/subcategorias', body)
  }

  const guardarProducto = () => {
    const body = {
      empresaCodigo: prod.empresaCodigo, nombre: prod.nombre,
      descripcion: prod.descripcion || undefined,
      valor: Number(prod.valor), stock: Number(prod.stock) || 0,
      subCategoriaNro: Number(prod.subCategoriaNro),
      productoDiaVenta: Number(prod.diaVenta) || 0,
      productoLibreValor1: Number(prod.libreValor1) || 0,
      productoLibreValor2: Number(prod.libreValor2) || 0,
      productoLibreValor3: Number(prod.libreValor3) || 0,
      productoLibreNumero1: Number(prod.libreNumero1) || 0,
      productoLibreNumero2: Number(prod.libreNumero2) || 0,
      productoLibreNumero3: Number(prod.libreNumero3) || 0,
      productoLibreCaracter1: prod.libreCaracter1 || undefined,
      productoLibreCaracter2: prod.libreCaracter2 || undefined,
      productoImagen: prod.imagen || undefined,
    }
    return editando
      ? api.put(`/catalogos/productos/${editando.productoNro}?empresa=${prod.empresaCodigo}`, body)
      : api.post('/catalogos/productos', body)
  }

  // Abrir modal en modo alta (categoriaww.cs:2548 'DO INSERT').
  const abrirNuevo = (tipo: 'cat' | 'sub' | 'prod') => {
    setEditando(null)
    setErrorModal('')
    if (tipo === 'cat') setCat({ empresaCodigo: empresa, nombre: '', descripcion: '', imagen: '' })
    if (tipo === 'sub') setSub({ empresaCodigo: empresa, nombre: '', categoriaNro: '', descripcion: '', imagen: '' })
    if (tipo === 'prod') setProd({
      empresaCodigo: empresa, nombre: '', descripcion: '', valor: '', stock: '', subCategoriaNro: '',
      diaVenta: '', libreValor1: '', libreValor2: '', libreValor3: '',
      libreNumero1: '', libreNumero2: '', libreNumero3: '',
      libreCaracter1: '', libreCaracter2: '', imagen: '',
    })
    setModal(tipo)
  }

  // [RGM] 2026-09-29 (pedido Rafael: "las imágenes deben ser muy pequeñas y
  // no pueden ocupar mucho espacio, debe comprimirlas"): se comprime en el
  // navegador con canvas (máx 500px de lado, JPEG calidad 0.7 → pocos KB)
  // antes de mandar el base64 (antes viajaba la foto completa sin comprimir).
  const leerImagen = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: string) => void) => {
    const file = e.target.files?.[0]
    if (!file) return
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const MAX = 500
      const escala = Math.min(1, MAX / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(img.width * escala))
      canvas.height = Math.max(1, Math.round(img.height * escala))
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(url); return }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const data = canvas.toDataURL('image/jpeg', 0.7)
      setter(data.replace(/^data:.*;base64,/, ''))
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  // Modificar (categoriaww.cs:2295-2301 'DO UPDATE'): carga la fila en el form
  // y abre el modal. La llave (EmpresaCodigo + Nro consecutivo) no cambia.
  const abrirEditar = (tipo: 'cat' | 'sub' | 'prod') => (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    if (tipo === 'cat') {
      setCat({
        empresaCodigo: fila.empresaCodigo ?? empresa,
        nombre: fila.categoriaNombre ?? '',
        descripcion: fila.categoriaDescripcion ?? '',
        imagen: '',
      })
    }
    if (tipo === 'sub') {
      setSub({
        empresaCodigo: fila.empresaCodigo ?? empresa,
        nombre: fila.subCategoriaNombre ?? '',
        categoriaNro: String(fila.categoriaNro ?? ''),
        descripcion: fila.subCategoriaDescripcion ?? '',
        imagen: '',
      })
    }
    if (tipo === 'prod') {
      setProd({
        empresaCodigo: fila.empresaCodigo ?? empresa,
        nombre: fila.productoNombre ?? '',
        descripcion: fila.productoDescripcion ?? '',
        valor: String(fila.productoValor ?? ''),
        stock: String(fila.productoStock ?? ''),
        subCategoriaNro: String(fila.subCategoriaNro ?? ''),
        diaVenta: String(fila.productoDiaVenta ?? ''),
        libreValor1: String(fila.productoLibreValor1 ?? ''),
        libreValor2: String(fila.productoLIbreValor2 ?? ''),
        libreValor3: String(fila.productoLibreValor3 ?? ''),
        libreNumero1: String(fila.productoLibreNumero1 ?? ''),
        libreNumero2: String(fila.productoLibreNumero2 ?? ''),
        libreNumero3: String(fila.productoLibreNumero3 ?? ''),
        libreCaracter1: fila.productoLibreCaracter1 ?? '',
        libreCaracter2: fila.productoLibreCaracter2 ?? '',
        imagen: '',
      })
    }
    setModal(tipo)
  }

  // Eliminar (categoriaww.cs:2303-2311 'DO DELETE'): confirm + DELETE con
  // empresa. El backend replica OnDeleteControls076 (categoria_bc.cs:478-492)
  // → 409 si hay SubCategorias; subcategoria_bc.cs:500-518 → 409 si Productos;
  // producto_bc.cs:605-627 → 409 si FacturaLineas.
  const eliminar = (tipo: 'cat' | 'sub' | 'prod') => (fila: any) => {
    const nombres: Record<string, string> = { cat: 'categoría', sub: 'subcategoría', prod: 'producto' }
    const recursos: Record<string, string> = { cat: 'categorias', sub: 'subcategorias', prod: 'productos' }
    const llaves: Record<string, string> = { cat: 'categoriaNro', sub: 'subCategoriaNro', prod: 'productoNro' }
    if (!window.confirm(`¿Eliminar ${nombres[tipo]} ${fila[llaves[tipo]]}?`)) return
    api.delete(`/catalogos/${recursos[tipo]}/${fila[llaves[tipo]]}?empresa=${empresa}`)
      .then(() => {
        setAviso(`Registro eliminado.`)
        cargar()
      })
      .catch((e: any) => setError(e.message))
  }

  const botonesModal = (ref: React.RefObject<HTMLFormElement | null>) => (
    <>
      <CButton color="secondary" type="button" onClick={() => setModal(null)} disabled={guardando}>Cancelar</CButton>
      <CButton color="primary" type="button" disabled={guardando} onClick={() => ref.current?.requestSubmit()}>{guardando ? <CSpinner size="sm" /> : 'Guardar'}</CButton>
    </>
  )

  // [RGM] 2026-09-29 (pedido Rafael): los formularios del módulo se organizan
  // en TARJETAS (mismo patrón de Préstamos) y las imágenes se ven en miniatura.
  const tarjeta = (titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className="fw-semibold py-2">{titulo}</CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  // Miniatura chica del base64 ya comprimido (56px — no ocupa espacio).
  const previewImagen = (base64: string, quitar: () => void) => (
    <div className="d-flex align-items-center gap-2">
      <img src={`data:image/jpeg;base64,${base64}`} alt="Vista previa"
        style={{ maxWidth: 56, maxHeight: 56, borderRadius: 6, border: '1px solid var(--cui-border-color)' }} />
      <CButton size="sm" color="danger" variant="outline" type="button" onClick={quitar}>Quitar</CButton>
    </div>
  )

  const [totalCategorias, setTotalCategorias] = useState(categorias.length)
  const [totalSubCategorias, setTotalSubCategorias] = useState(subCategorias.length)
  const [totalProductos, setTotalProductos] = useState(productos.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Categoria</h4>
          <small className="text-body-secondary">{totalCategorias + totalSubCategorias + totalProductos} {totalCategorias + totalSubCategorias + totalProductos === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      <CNav variant="tabs" className="mb-3" role="tablist">
        <CNavItem>
          <CNavLink active={tab === 'categorias'} onClick={() => setTab('categorias')}>Categoria</CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink active={tab === 'subcategorias'} onClick={() => setTab('subcategorias')}>Sub Categoria</CNavLink>
        </CNavItem>
        <CNavItem>
          <CNavLink active={tab === 'productos'} onClick={() => setTab('productos')}>Producto</CNavLink>
        </CNavItem>
      </CNav>

      <CTabContent>
        <CTabPane visible={tab === 'categorias'}>
          <WwGrid
            title="Categoria"
            columns={columnasCategorias}
            rows={categorias}
        onTotalChange={setTotalCategorias}
            loading={cargando}
            onAgregar={() => abrirNuevo('cat')}
            onEditar={abrirEditar('cat')}
            onEliminar={eliminar('cat')}
            // [RGM] 2026-10-05 (pedido Rafael: "a las filas ... debe llevar al
            // view"): clic en la FILA abre la vista de detalle.
            onRowClick={(row) => navigate('/catalogos/categorias/' + row.categoriaNro)}
          />
        </CTabPane>

        <CTabPane visible={tab === 'subcategorias'}>
          <WwGrid
            title="Sub Categoria"
            columns={columnasSubCategorias}
            rows={subCategorias}
        onTotalChange={setTotalSubCategorias}
            loading={cargando}
            onAgregar={() => abrirNuevo('sub')}
            onEditar={abrirEditar('sub')}
            onEliminar={eliminar('sub')}
            // [RGM] 2026-10-05 (pedido Rafael: "a las filas ... debe llevar al
            // view"): clic en la FILA abre la vista de detalle.
            onRowClick={(row) => navigate('/catalogos/subcategorias/' + row.subCategoriaNro)}
          />
        </CTabPane>

        <CTabPane visible={tab === 'productos'}>
          <WwGrid
            title="Producto"
            columns={columnasProductos}
            rows={productos}
        onTotalChange={setTotalProductos}
            loading={cargando}
            onAgregar={() => abrirNuevo('prod')}
            onEditar={abrirEditar('prod')}
            onEliminar={eliminar('prod')}
            // [RGM] 2026-10-05 (pedido Rafael: "a las filas ... debe llevar al
            // view"): clic en la FILA abre la vista de detalle.
            onRowClick={(row) => navigate('/catalogos/productos/' + row.productoNro)}
          />
        </CTabPane>
      </CTabContent>

      <CModal visible={modal === 'cat'} onClose={() => { setModal(null); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar categoría' : 'Nueva categoría'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={catFormRef} onSubmit={(e) => guardar(e, guardarCategoria)}>
            {tarjeta('Categoría', (
              <>
                {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={cat.nombre} onChange={(e) => setCat({ ...cat, nombre: e.target.value.toUpperCase() })} /></CCol>
                <CCol md={6}><CFormLabel>Descripción</CFormLabel><CFormInput value={cat.descripcion} onChange={(e) => setCat({ ...cat, descripcion: e.target.value })} onBlur={(e) => setCat({ ...cat, descripcion: capitalizarPalabras(e.target.value) })} /></CCol>
              </>
            ))}
            {tarjeta('Imagen (se comprime automáticamente)', (
              <>
                <CCol md={12}><CFormInput type="file" accept="image/*" onChange={(e) => leerImagen(e, (v) => setCat({ ...cat, imagen: v }))} /></CCol>
                {cat.imagen && <CCol md={12}>{previewImagen(cat.imagen, () => setCat({ ...cat, imagen: '' }))}</CCol>}
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">{botonesModal(catFormRef)}</CModalFooter>
      </CModal>

      <CModal visible={modal === 'sub'} onClose={() => { setModal(null); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar subcategoría' : 'Nueva subcategoría'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={subFormRef} onSubmit={(e) => guardar(e, guardarSubCategoria)}>
            {tarjeta('SubCategoría', (
              <>
                {/* [RGM] 2026-09-29 (pedido Rafael: "acomoda los selects...
                    están en número"): la categoría ahora es SELECT con nombre. */}
                <CCol md={6}>
                  <CFormLabel>Categoría</CFormLabel>
                  <CFormSelect value={sub.categoriaNro} onChange={(e) => setSub({ ...sub, categoriaNro: e.target.value })}>
                    <option value="">Elegir categoría…</option>
                    {categorias.map((c) => (
                      <option key={c.categoriaNro} value={String(c.categoriaNro)}>
                        {c.categoriaNombre?.trim() || `Categoría ${c.categoriaNro}`}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={sub.nombre} onChange={(e) => setSub({ ...sub, nombre: e.target.value.toUpperCase() })} /></CCol>
                <CCol md={12}><CFormLabel>Descripción</CFormLabel><CFormInput value={sub.descripcion} onChange={(e) => setSub({ ...sub, descripcion: e.target.value })} onBlur={(e) => setSub({ ...sub, descripcion: capitalizarPalabras(e.target.value) })} /></CCol>
              </>
            ))}
            {tarjeta('Imagen (se comprime automáticamente)', (
              <>
                <CCol md={12}><CFormInput type="file" accept="image/*" onChange={(e) => leerImagen(e, (v) => setSub({ ...sub, imagen: v }))} /></CCol>
                {sub.imagen && <CCol md={12}>{previewImagen(sub.imagen, () => setSub({ ...sub, imagen: '' }))}</CCol>}
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">{botonesModal(subFormRef)}</CModalFooter>
      </CModal>

      <CModal visible={modal === 'prod'} onClose={() => { setModal(null); setEditando(null) }} size="lg">
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar producto' : 'Nuevo producto'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={prodFormRef} onSubmit={(e) => guardar(e, guardarProducto)}>
            {tarjeta('Producto', (
              <>
                {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={prod.nombre} onChange={(e) => setProd({ ...prod, nombre: e.target.value.toUpperCase() })} /></CCol>
                <CCol md={6}><CFormLabel>Descripción</CFormLabel><CFormInput value={prod.descripcion} onChange={(e) => setProd({ ...prod, descripcion: e.target.value })} onBlur={(e) => setProd({ ...prod, descripcion: capitalizarPalabras(e.target.value) })} /></CCol>
                {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo de
                    la moneda + miles en vivo SOLO en campos de dinero (Valor y
                    libres de valor); Stock/Día/Números no llevan moneda. */}
                <CCol md={3}>
                  <CFormLabel>Valor</CFormLabel>
                  <CInputGroup>
                    <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                    <NumericFormat
                      className="form-control"
                      value={prod.valor}
                      onValueChange={(v) => setProd({ ...prod, valor: v.value })}
                      thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                      allowNegative={false} inputMode="decimal"
                    />
                  </CInputGroup>
                </CCol>
                <CCol md={3}><CFormLabel>Stock</CFormLabel><CFormInput type="number" value={prod.stock} onChange={(e) => setProd({ ...prod, stock: e.target.value })} /></CCol>
                {/* [RGM] 2026-10-05 (pedido Rafael: "lo de día venta parece un
                    select"): combo GX con los 7 días — labels EXACTOS del GX
                    (producto.cs:3128-3135), opción vacía = 0 (sin día). */}
                <CCol md={3}>
                  <CFormLabel>Día Venta</CFormLabel>
                  <CFormSelect value={prod.diaVenta} onChange={(e) => setProd({ ...prod, diaVenta: e.target.value })}>
                    <option value="">Elegir día…</option>
                    <option value="1">Lunes</option>
                    <option value="2">Martes</option>
                    <option value="3">Miercoles</option>
                    <option value="4">Jueves</option>
                    <option value="5">Viernes</option>
                    <option value="6">Sabado</option>
                    <option value="7">Domingo</option>
                  </CFormSelect>
                </CCol>
              </>
            ))}
            {tarjeta('Clasificación', (
              <>
                {/* [RGM] 2026-09-29 (pedido Rafael: "acomoda los selects...
                    están en número"): la subcategoría ahora es SELECT. */}
                <CCol md={6}>
                  <CFormLabel>Sub Categoría</CFormLabel>
                  <CFormSelect value={prod.subCategoriaNro} onChange={(e) => setProd({ ...prod, subCategoriaNro: e.target.value })}>
                    <option value="">Elegir subcategoría…</option>
                    {subCategorias.map((s) => (
                      <option key={s.subCategoriaNro} value={String(s.subCategoriaNro)}>
                        {s.subCategoriaNombre?.trim() || `SubCategoría ${s.subCategoriaNro}`}
                        {s.categoriaNombre?.trim() ? ` · ${s.categoriaNombre.trim()}` : ''}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
              </>
            ))}
            {tarjeta('Imagen (se comprime automáticamente)', (
              <>
                <CCol md={12}><CFormInput type="file" accept="image/*" onChange={(e) => leerImagen(e, (v) => setProd({ ...prod, imagen: v }))} /></CCol>
                {prod.imagen && <CCol md={12}>{previewImagen(prod.imagen, () => setProd({ ...prod, imagen: '' }))}</CCol>}
              </>
            ))}
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">{botonesModal(prodFormRef)}</CModalFooter>
      </CModal>
    </>
  )
}

export default CatalogosPage
